import React, { useContext, useEffect, useState } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { AircraftDetail, TechlogDetail } from 'types/techlog'
import useQuery from 'utils/hooks/useQuery'
import urlApis from 'utils/api/urlApis'
import LangContext from 'context/LangContext'
import ComponentsUNames from 'constants/componentsUNames'
import LocalStorage from 'utils/myLocalStorage'
import { DisplayModus } from 'constants/constants'
import TechlogNavigator from './TechlogNavigator'
import TechlogForm from './TechlogForm'
import PageHeader from 'components/pageHeader'
import { faPencil } from '@fortawesome/free-solid-svg-icons'
import { faSquarePlus } from '@fortawesome/free-regular-svg-icons'
import { Chip, Container, IconButton, Inline } from '@airbus/components-react/components'
import { NavItem } from 'types/navItems'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'

const TechlogPage = () => {
  const baseUrl = process.env.PUBLIC_URL
  const { t } = useTranslation()
  const { lang } = useContext(LangContext)
  const { avimmatr = null, rmdate = null, rmdoc = null } = useParams()
  const navigate = useNavigate()
  const location = useLocation()

  const ActionButtons = ({
    displayMode,
    setDisplayMode,
    isLoading = false,
  }: {
    displayMode: DisplayModus
    setDisplayMode: (b: DisplayModus) => void
    isLoading: boolean
  }) => {
    const { t } = useTranslation()

    const isEditMode = [DisplayModus.WRITE, DisplayModus.CREATE].includes(displayMode)

    return (
      <Inline>
        {!!rmdoc && !isEditMode && canCreate && (
          <IconButton
            variant='ghost'
            size='xsmall'
            onClick={() => {
              navigate(`/amasis-web/techlogs/${avimmatr}/new`)
            }}
            title={t('9500160')}
            disabled={isLoading}
          >
            <FontAwesomeIcon icon={faSquarePlus} />
          </IconButton>
        )}
        {!!rmdoc && !isEditMode && canUpdate && (
          <IconButton
            variant='ghost'
            size='xsmall'
            onClick={() => {
              setDisplayMode(DisplayModus.WRITE)
            }}
            title={t('7000398')}
            disabled={isLoading}
          >
            <FontAwesomeIcon icon={faPencil} />
          </IconButton>
        )}
      </Inline>
    )
  }

  const pathName = location.pathname
  let navItems: NavItem[] = []
  if (pathName.includes('fleetpan')) {
    navItems = [
      {
        title: t('9500050'),
        url: baseUrl + '/fleetpan',
      },
      { title: avimmatr as string, url: baseUrl + '/fleetpan/' + avimmatr },
      { title: rmdoc ? rmdate + ' - ' + rmdoc : '', url: window.location.href },
    ]
  } else if (pathName.includes('techlogs')) {
    navItems = [
      {
        title: t('7001825'),
        url: baseUrl + '/techlogs',
      },
      {
        title: avimmatr as string,
        url: baseUrl + '/techlogs?search=' + avimmatr + (rmdoc && rmdoc !== '' ? '&id=' + rmdoc : ''),
      },
      { title: rmdoc ? rmdate + ' - ' + rmdoc : '', url: window.location.href },
    ]
  }

  const [displayMode, setDisplayMode] = useState(rmdoc ? DisplayModus.READ : DisplayModus.CREATE)

  const [data, setData] = useState<TechlogDetail>()
  const [aircraftDetails, setAircraftDetails] = useState<AircraftDetail>()
  const { fetchData: fetchAircraft } = useQuery<AircraftDetail>(urlApis.URL_CRM_AC_GET)
  const { fetchData: fetchTechlog } = useQuery<TechlogDetail>(urlApis.URL_CRM_BYFLIGHT_GET)
  const { fetchData: fetchLastTechlog } = useQuery<TechlogDetail>(urlApis.URL_CRM_BYFLIGHT_LAST_GET)

  const canCreate = LocalStorage.getOrDefault(
    [LocalStorage.KEY_PERMISSIONS, ComponentsUNames.CRM_CREATE_PERMISSIONS],
    LocalStorage.VALUE_TYPE.BOOLEAN,
    false,
  )

  const canUpdate = LocalStorage.getOrDefault(
    [LocalStorage.KEY_PERMISSIONS, ComponentsUNames.CRM_UPDATE_PERMISSIONS],
    LocalStorage.VALUE_TYPE.BOOLEAN,
    false,
  )

  const canView = LocalStorage.getOrDefault(
    [LocalStorage.KEY_PERMISSIONS, ComponentsUNames.CRM_DISPLAY_PERMISSIONS],
    LocalStorage.VALUE_TYPE.BOOLEAN,
    false,
  )

  const loadTechlog = async (avnumap: string, rmdate: string, rmdoc: string) => {
    const techlogResponse = await fetchTechlog({
      lang: lang,
      avnumap: avnumap,
      rmdate: rmdate ? rmdate.substring(0, 4) + '-' + rmdate.substring(4, 6) + '-' + rmdate.substring(6) : null,
      rmdoc: rmdoc,
    })
    setData(techlogResponse?.data)
  }

  const loadLastTechlog = async (avnumap: string, aircraftDetails: AircraftDetail) => {
    const techlogResponse = await fetchLastTechlog({
      lang: lang,
      avnumap: avnumap,
    })
    const dataLastTechlog = techlogResponse?.data
    setData({
      ...dataLastTechlog,
      flightDetail: [
        {
          voord: 1,
          avnumap: dataLastTechlog?.flightDetail[0].avnumap || aircraftDetails?.avnumap,
          avimmatr: dataLastTechlog?.flightDetail[0].avimmatr || aircraftDetails?.avimmatr,
          voaerodep: dataLastTechlog ? dataLastTechlog.flightDetail[0].voaeroarr : aircraftDetails?.escale,
          ketcatd: dataLastTechlog?.flightDetail[0].ketcatp,
        },
      ],
    })
  }

  const handleClosePage = () => {
    if (displayMode === DisplayModus.CREATE) {
      navigate(-1)
      /*    } else {
      if (avnumap && rmdoc) {
        loadTechlog(avnumap, rmdoc)
      }
        */
    } else {
      setDisplayMode(DisplayModus.READ)
    }
  }

  /**
   * Finalizing successful creation | update of the form
   */
  const handleActionCompleted = () => {
    if (displayMode === DisplayModus.CREATE) {
      const urlToNavigate = baseUrl + '/techlogs?search=' + aircraftDetails?.avimmatr
      navigate(urlToNavigate)
    } else {
      navigate(0)
    }
  }

  const loadAircraftDetailsAndTechlog = async (avimmatr: string) => {
    const aircraftResponse = await fetchAircraft({
      lang,
      avimmatr: avimmatr,
    })
    const aircraftDetails = aircraftResponse?.data[0]
    setAircraftDetails(aircraftDetails)
    const avnumap = aircraftDetails?.avnumap
    if (avnumap) {
      if (rmdate && rmdoc) {
        setDisplayMode(DisplayModus.READ)
        loadTechlog(avnumap, rmdate, rmdoc)
      } else {
        // Switch to create mode
        setDisplayMode(DisplayModus.CREATE)
        loadLastTechlog(avnumap, aircraftDetails)
      }
    }
  }

  useEffect(() => {
    if (avimmatr) {
      // Load aircraft's details (avnumap, applicability) : these info are required and used by the TL
      loadAircraftDetailsAndTechlog(avimmatr)
    }
  }, [avimmatr, rmdoc])

  if (!canCreate && !canView) return <div style={{ paddingLeft: '1ex' }}>{t('9500138')}</div>

  return (
    <>
      <PageHeader
        pageTitle={
          <Inline spacing='1-x'>
            <span>{rmdoc ? t('9500159') : t('9500160')}</span>
            {avimmatr && <Chip label={avimmatr} />}
            {rmdoc && <Chip label={rmdoc} />}
          </Inline>
        }
        navItems={navItems}
        ActionButtons={({ isLoading }) => (
          <ActionButtons isLoading={isLoading} displayMode={displayMode} setDisplayMode={setDisplayMode} />
        )}
      />
      <Container style={{ flex: 1 }} until='xl'>
        <TechlogNavigator displayMode={displayMode} techlog={data} />
        <TechlogForm
          lang={lang}
          displayMode={displayMode}
          aircraft={aircraftDetails}
          lastCrm={data?.lastCrm as string}
          flightDetail={data?.flightDetail[0]}
          onClose={handleClosePage}
          onActionCompleted={handleActionCompleted}
        />
      </Container>
    </>
  )
}

export default TechlogPage
